import type { SearchMembersFn } from '@/features/iam'
import { computed, observable, action, runInAction } from 'mobx'
import { validationContext, func } from 'validx'
import { AllocationInputViewModel } from '@/features/jobs/details/components/PayoutSplitAllocator/AllocationInputView/AllocationInputViewModel'
import currency from 'currency.js'
import { type AllocationInput } from '@/features/jobs/JobStore'
import { type Id } from '@/infra/id'

/**
 * View model for managing a payout split.
 */
export class PayoutSplitAllocatorViewModel {
  /**
   * List of allocations.
   */
  @observable accessor allocations: AllocationInputViewModel[] = []

  /**
   * Validation.
   */
  readonly validation = validationContext(this, {
    allocations: [
      func(() => {
        if (this.allocations.length === 0) {
          return 'At least one allocation is required'
        }

        if (!this.isSplitValid) {
          return `The allocations must add up to the total payout amount`
        }

        return true
      }),
    ],
  })

  constructor(
    private readonly _workspaceId: Id,
    private readonly _getPayoutTotal: () => number,
    private readonly _findMembers: SearchMembersFn,
  ) {}

  /**
   * The payout total to allocate.
   */
  @computed
  get payoutTotal() {
    return this._getPayoutTotal()
  }

  /**
   * The allocated total based on current allocations.
   */
  @computed
  get allocatedTotal() {
    return this.allocations.reduce(
      (accum, allocation) => accum.add(allocation.allocatedAmount.value),
      currency(0),
    ).value
  }

  /**
   * Whether the currently defined split is considered valid.
   */
  @computed
  get isSplitValid() {
    if (this.allocations.length === 0) {
      return false
    }

    const payoutTotal = this._getPayoutTotal()
    return this.allocatedTotal === payoutTotal
  }

  /**
   * Whether the user is able to add another allocation.
   */
  @computed
  get canAddAllocation() {
    // Only allow adding another allocation if a recipient has been
    // chosen for all the allocations currently in the list.
    return this.allocations.every((a) => a.recipient)
  }

  /**
   * Whether the user is able to remove another allocation.
   */
  @computed
  get canRemoveAllocation() {
    //  Only allow removing allocations if there's at least one
    // allocation left.
    return this.allocations.length > 1
  }

  /**
   * Whether it is currently possible to fill the remainder.
   */
  @computed
  get canFillRemainder() {
    const spaceToFill = this.payoutTotal > this.allocatedTotal
    const hasAllocationsWorthZero =
      this.allocations.filter((a) => a.allocatedAmount.value === 0).length > 0

    return this.allocations.length > 2
      ? spaceToFill && hasAllocationsWorthZero
      : spaceToFill
  }

  /**
   * Resets the state of the view model.
   */
  @action.bound
  reset() {
    this.allocations = []
    this.validation.reset()
  }

  /**
   * Validates the split.
   */
  @action.bound
  validate() {
    const allAllocationsValid = this.allocations.every((a) => a.validate())
    const splitValid = this.validation.validate().isValid
    return allAllocationsValid && splitValid
  }

  /**
   * Adds another allocation.
   */
  @action.bound
  addAllocation() {
    this.allocations.push(this._createAllocationVm())
  }

  /**
   * Gets the allocations that have been defined.
   */
  getAllocations() {
    const result: AllocationInput[] = []
    for (const allocation of this.allocations) {
      if (!allocation.recipient || allocation.allocatedAmount.value === 0) {
        continue
      }

      result.push({
        user: allocation.recipient,
        allocationAmount: allocation.allocatedAmount.value,
        authorizedPartialPayoutAmount:
          allocation.authorizedPartialPayoutAmount.value,
      })
    }

    return result
  }

  /**
   * Sets the allocations.
   */
  @action.bound
  setAllocations(allocations: AllocationInput[]) {
    this.allocations = allocations.map((input) => {
      const vm = this._createAllocationVm()
      vm.setRecipient(input.user)
      vm.allocatedAmount.setValue(input.allocationAmount)
      vm.authorizedPartialPayoutAmount.setValue(
        input.authorizedPartialPayoutAmount,
      )
      return vm
    })
  }

  /**
   * Removes the specified allocation.
   */
  @action.bound
  removeAllocation(vm: AllocationInputViewModel) {
    this.allocations.splice(this.allocations.indexOf(vm), 1)
  }

  /**
   * Splits the payout equally between the current allocations.
   */
  @action.bound
  splitEqually() {
    if (!this.allocations.length) {
      return
    }

    PayoutSplitAllocatorViewModel._splitEquallyAcross(
      this.payoutTotal,
      this.allocations,
    )
  }

  /**
   * Fills in whatever amount is remaining by spreading it out across allocations.
   */
  @action.bound
  fillRemainder() {
    if (!this.canFillRemainder) {
      return
    }

    switch (this.allocations.length) {
      // When there is just a single allocation, we allocate 100% to that one.
      case 1:
        this.allocations[0].allocatedAmount.setValue(this.payoutTotal)
        break

      // When there are only two allocations, "fill remainder" applies to the last one.
      case 2:
        PayoutSplitAllocatorViewModel._splitEquallyAcross(
          currency(this.payoutTotal).subtract(
            this.allocations[0].allocatedAmount.value,
          ).value,
          [this.allocations[1]],
        )
        break

      default:
        // When there are more than two, it applies to the allocations with zero.
        PayoutSplitAllocatorViewModel._splitEquallyAcross(
          currency(this.payoutTotal).subtract(this.allocatedTotal).value,
          this.allocations.filter((a) => a.allocatedAmount.value === 0),
        )
    }
  }

  /**
   * Splits the given total equally across the given allocations.
   *
   * @param total
   * The total to split.
   *
   * @param allocations
   * The allocations to split the total across.
   */
  private static _splitEquallyAcross(
    total: number,
    allocations: AllocationInputViewModel[],
  ) {
    runInAction(() => {
      const distribution = currency(total).distribute(allocations.length)
      allocations.forEach((a, i) =>
        a.allocatedAmount.setValue(distribution[i].value),
      )
    })
  }

  /**
   * Creates a new allocation VM.
   */
  private _createAllocationVm() {
    const vm: AllocationInputViewModel = new AllocationInputViewModel(
      this._workspaceId,
      this._getPayoutTotal,
      this._findMembers,
      () => this.removeAllocation(vm),
      (member) =>
        this.allocations.some((a) => a !== vm && a.recipient === member),
    )

    return vm
  }
}
